import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Person as PersonIcon, Settings as SettingsIcon } from "@mui/icons-material";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import moment from "moment";

/**
 * Composant tableau pour afficher les relevés d'index
 * @param {Object} props
 * @param {Array} props.readings - Liste des relevés d'index
 * @param {Function} props.onRowClick - Fonction appelée au clic sur une ligne
 * @param {Array} props.selectedItems - IDs des éléments sélectionnés
 * @param {Function} props.setSelectedItems - Fonction pour mettre à jour les éléments sélectionnés
 * @param {Function} props.setSelectedRows - Fonction pour mettre à jour les lignes sélectionnées
 * @param {Array} props.actions - Actions de la toolbar (optionnel)
 */
export default function IndexReadings(props) {
  const { t } = useTranslation("common");
  const {
    readings = [],
    onRowClick,
    selectedItems = [],
    setSelectedItems,
    setSelectedRows,
    actions = [],
  } = props;

  const columns = [
    {
      name: "reading_date",
      label: "Date",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return moment(value).format("DD/MM/YYYY HH:mm");
        },
      },
    },
    {
      name: "index_value",
      label: "Index",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const reading = readings[tableMeta.rowIndex];
          const unit = getUnitFromDevice(reading);
          return (
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: "var(--color-primary)",
                  fontFamily: "var(--font-screen)",
                  fontSize: "1rem",
                  letterSpacing: "0.01em",
                }}
              >
                {parseFloat(value).toFixed(3)} <span style={{ opacity: 0.7, fontSize: "0.85rem", fontWeight: 500 }}>{unit}</span>
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "reading_type",
      label: "Type",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const isManual = value === "MANUAL";
          return (
            <Chip
              icon={isManual ? <PersonIcon /> : <SettingsIcon />}
              label={isManual ? "Manuel" : "Auto"}
              size="small"
              sx={{
                fontWeight: 500,
                backgroundColor: isManual ? "rgba(38, 166, 154, 0.2)" : "rgba(255, 255, 255, 0.1)",
                color: isManual ? "#26A69A" : "text.secondary",
                border: `1px solid ${isManual ? "rgba(38, 166, 154, 0.4)" : "rgba(255, 255, 255, 0.1)"}`,
                "& .MuiChip-icon": {
                  color: isManual ? "#26A69A" : "text.secondary"
                }
              }}
            />
          );
        },
      },
    },
    {
      name: "source",
      label: "Source",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography variant="body2" sx={{ maxWidth: 200 }}>
              {value || "-"}
            </Typography>
          );
        },
      },
    },
    {
      name: "notes",
      label: "Notes",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value) return "-";
          return (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "created_by_name",
      label: "Créé par",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography variant="body2" color="text.secondary">
              {value || "-"}
            </Typography>
          );
        },
      },
    },
    {
      name: "created_at",
      label: "Créé le",
      options: {
        filter: false,
        sort: true,
        display: false, // Masqué par défaut
        customBodyRender: (value) => {
          return moment(value).format("DD/MM/YYYY HH:mm");
        },
      },
    },
  ];

  /**
   * Détermine l'unité à afficher en fonction du device
   * TODO: À adapter selon la structure réelle des données
   */
  function getUnitFromDevice(reading) {
    // Pour l'instant, on retourne m³ par défaut
    // Pourrait être enrichi avec reading.device_type, etc.
    return "m³";
  }

  return (
    <DataTableBase
      title="Relevés d'index"
      data={readings}
      columns={columns}
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      setSelectedRows={setSelectedRows}
      onRowClick={onRowClick}
      actions={actions}
      selectableRows={actions.length > 0 ? "multiple" : "none"}
      sortOrder="reading_date"
      sortDirection="desc"
    />
  );
}

/**
 * Fonction helper pour déterminer l'unité en fonction du type de device
 * @param {string} deviceType - Type du device
 * @returns {string} - Unité (m³, kWh, etc.)
 */
export function getDeviceUnit(deviceType) {
  if (!deviceType) return "m³";

  const type = deviceType.toLowerCase();
  if (type.includes("water") || type.includes("eau")) {
    return "m³";
  } else if (type.includes("electric") || type.includes("électric")) {
    return "kWh";
  } else if (type.includes("gas") || type.includes("gaz")) {
    return "m³";
  }

  return "m³"; // Par défaut
}
